import React from 'react'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreative, Campaign, DraftCampaign, LeadCard, Sprint } from '@marketing-milk/interfaces'
import { Add } from '@material-ui/icons'
import { Upload } from '@mui/icons-material'
import { Button } from '@mui/material'
import { MMAlert } from 'components/Alert'
import { BlankPageLayout } from 'components/BlankPageLayout'
import { ConfirmationModal } from 'components/ConfirmationModal'
import { addMonths, endOfDay, getUnixTime } from 'date-fns'
import { useDraftCampaign } from 'hooks/api/useDraftCampaign'
import { useSprint } from 'hooks/api/useSprint'
import { useSprintPackets } from 'hooks/api/useSprintPackets'
import { useFetchAllAudiences } from 'hooks/audiences/useFetchAllAudiences'
import { any } from 'lodash/fp'
import { packetRejectionsUrl } from 'pages/business/creatives/creativeUrl'
import { ErrorToolTip } from 'pages/business/campaigns/Campaigns'
import {
  getCampaignErrorMessage,
  isValidCreative,
} from 'pages/business/campaigns/helpers/campaign.helpers'
import {
  getDefaultDTO,
  isValidCampaignDto,
} from 'pages/business/campaigns/helpers/campaignForm.helpers'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { DraftCampaignsTable } from './DraftCampaignsTable'
import { EditDraftCampaignModal } from './EditDraftCampaignModal'
import { EditLeadFormsModal } from './EditLeadFormsModal'
import { PublishDraftCampaignsModal } from './PublishDraftCampaigns'

interface SprintsCampaignsTabProps {
  sprint: Sprint
  campaigns?: Campaign[]
  creatives?: AdCreative[]
}

// Creatives a sibling draft has claimed. The picker shows them by default — publishing the same
// ad to more than one campaign in a sprint is something the analysts do — and offers this set as
// the thing its "include ads from other campaigns" checkbox hides, for partitioning a sprint.
const getCreativeIDsUsedByOtherDrafts = (
  draft: DraftCampaign,
  allDrafts: DraftCampaign[] | undefined
): Set<number> => {
  const ownIDs = new Set(draft.config.creativeGroups.flat())
  return new Set(
    (allDrafts ?? [])
      .filter(d => d.id !== draft.id)
      .flatMap(d => d.config.creativeGroups.flat())
      .filter((id): id is number => id !== null && !ownIDs.has(id))
  )
}

export const SprintDraftCampaigns = (props: SprintsCampaignsTabProps) => {
  const businessID = useGetBusinessID()

  const [addCampaignOpen, setAddCampaignOpen] = useState(false)

  const { createDraftCampaign, updateDraftCampaign } = useDraftCampaign()
  const {
    draftCampaigns,
    sprintCreatives,
    sprintCreativesLoading,
    refetchCreatives,
    refetchDraftCampagins,
    refetchPublishedCampaigns,
  } = useSprint(props.sprint.id)
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [draftCampaignToEdit, setDraftCampaignToEdit] = useState<DraftCampaign>()
  const [editType, setEditType] = useState<'audiences' | 'creatives' | 'settings' | 'leadForms'>()
  // Kept here rather than in the lead-forms drawer, which unmounts when it closes: a form created
  // there whose first assignment failed is on no ad and may be too new for the recent-forms
  // fetch, so closing the drawer would otherwise lose it. Per draft, for this page's lifetime.
  const [createdFormsByDraft, setCreatedFormsByDraft] = useState<Record<number, LeadCard[]>>({})

  const {
    audiences = [],
    defaultAudiences,
    isLoading: audiencesLoading,
  } = useFetchAllAudiences(businessID)

  // Resolved once here rather than per table row: the draft table's route into review needs
  // the packet's id, and a sprint has at most one packet. A sprint whose packet hasn't been
  // created yet simply has no review link to offer.
  const { list: sprintPackets } = useSprintPackets()
  const packetId = sprintPackets?.find(packet => packet.sprintID === props.sprint.id)?.id
  // Both routes out of a rejection go to the same place: the packet's rejected ads.
  const reviewHref = packetId ? packetRejectionsUrl(businessID, packetId) : undefined

  const campaignUploadErrorMessages = getCampaignErrorMessage(audiences)

  const getValidDraftCampaigns = () => {
    if (!sprintCreatives) return []
    if (!audiences) return []
    if (!draftCampaigns) return []
    const campaigns =
      draftCampaigns.filter(({ config }) =>
        isValidCampaignDto(config, sprintCreatives, audiences)
      ) || []
    return campaigns
  }

  const validDraftCampaigns = getValidDraftCampaigns()
  const canUpload = validDraftCampaigns.length > 0
  const publishHelpText =
    audiencesLoading || sprintCreativesLoading || !draftCampaigns
      ? 'Checking campaigns…'
      : draftCampaigns.length === 0
      ? 'Add a draft campaign to get started.'
      : !canUpload
      ? 'Resolve the errors in at least one campaign to publish.'
      : `${validDraftCampaigns.length} of ${draftCampaigns.length} draft campaigns ready to publish.`

  return (
    <BlankPageLayout stretch>
      <div className="space-y-2 py-2 mx-auto max-w-7xl">
        {sprintCreatives && sprintCreatives.length > 0 && !any(isValidCreative, sprintCreatives) && (
          <MMAlert type="warning">
            <p className="w-full">
              Your sprint has {sprintCreatives.filter(c => !isValidCreative(c)).length} incomplete
              creatives.
            </p>
          </MMAlert>
        )}
        {sprintCreatives && sprintCreatives.length === 0 && (
          <MMAlert type="warning">
            <p className="w-full">This sprint does not have any creatives available to use</p>
          </MMAlert>
        )}
        {!audiencesLoading && audiences.length === 0 && (
          <MMAlert type="warning">
            <p className="w-full">Your business does not have any audiences created</p>
          </MMAlert>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 w-full">
        <ConfirmationModal
          openState={[addCampaignOpen, setAddCampaignOpen]}
          warningText="This could put the sprint over budget"
          confirmButtonText="Add Draft Campaign To Sprint"
          onConfirm={async () => {
            await createDraftCampaign({
              sprintID: props.sprint.id,
              config: getDefaultDTO({
                creativeGroups: [[]],
                audienceIDs: defaultAudiences.map(({ id }) => id),
                campaign: {
                  budget: 150,
                  startTime: getUnixTime(endOfDay(new Date())),
                  endTime: getUnixTime(endOfDay(addMonths(new Date(), 3))),
                  frequency: 3,
                  name: `${props.sprint.name} | Campaign ${(draftCampaigns?.length || 0) + 1}`,
                },
              }),
            })
            refetchDraftCampagins()
            toast.success('Draft Uploaded! This may take a few seconds to reflect in the U.I.')
          }}
        />
        <Button
          variant="outlined"
          color="primary"
          onClick={async () => {
            setAddCampaignOpen(true)
          }}
        >
          <Add />
          <p className="ml-2">Add Draft Campaign</p>
        </Button>

        <PublishDraftCampaignsModal
          sprintId={props.sprint.id}
          creatives={sprintCreatives}
          audiences={audiences}
          draftCampaigns={validDraftCampaigns}
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
        />

        <ErrorToolTip title={!canUpload ? publishHelpText : ''}>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setPublishModalOpen(true)}
              disabled={!canUpload}
            >
              <Upload />
              <p className="ml-2">Select Campaigns to Publish</p>
            </Button>
          </div>
        </ErrorToolTip>
        <p className="w-full text-sm text-mm-neutral-1-600" role="status">
          {publishHelpText}
        </p>
      </div>

      {draftCampaignToEdit && editType !== 'leadForms' && (
        <EditDraftCampaignModal
          audiences={audiences}
          sprintCreatives={sprintCreatives}
          creativeIDsUsedByOtherDrafts={getCreativeIDsUsedByOtherDrafts(
            draftCampaignToEdit,
            draftCampaigns
          )}
          isOpen={!!draftCampaignToEdit}
          onClose={() => {
            setDraftCampaignToEdit(undefined)
          }}
          type={editType}
          reviewHref={reviewHref}
          initialDto={draftCampaignToEdit.config}
          updateDto={async dto => {
            if (!draftCampaignToEdit) return
            await updateDraftCampaign(draftCampaignToEdit.id, {
              sprintID: props.sprint.id,
              config: dto,
            })
            refetchDraftCampagins()
            toast.success('Draft Updated')
          }}
        />
      )}

      {draftCampaignToEdit && editType === 'leadForms' && (
        <EditLeadFormsModal
          isOpen
          onClose={() => setDraftCampaignToEdit(undefined)}
          campaignName={draftCampaignToEdit.config.campaign.name}
          creatives={(sprintCreatives ?? []).filter(c =>
            new Set(draftCampaignToEdit.config.creativeGroups.flat()).has(c.id)
          )}
          onAssigned={refetchCreatives}
          rememberedForms={createdFormsByDraft[draftCampaignToEdit.id]}
          rememberForms={forms =>
            setCreatedFormsByDraft(byDraft => ({
              ...byDraft,
              [draftCampaignToEdit.id]: forms,
            }))
          }
        />
      )}

      <DraftCampaignsTable
        draftCampaigns={draftCampaigns}
        openModal={(type, draft) => {
          setEditType(type)
          setDraftCampaignToEdit(draft)
        }}
        audiences={audiences}
        sprintCreatives={sprintCreatives}
        sprintId={props.sprint.id}
        reviewHref={reviewHref}
        isLoadingData={audiencesLoading || sprintCreativesLoading}
      />
    </BlankPageLayout>
  )
}
